import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
} from '@mui/material';
import {
  Assignment as TaskIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  PlayArrow as InProgressIcon,
  Warning as OverdueIcon,
} from '@mui/icons-material';

const TaskStats = ({ stats, loading }) => {
  const statCards = [
    {
      title: 'Total Tasks',
      value: stats?.totalTasks || 0,
      icon: TaskIcon,
      color: '#1976d2',
      bgColor: '#e3f2fd',
    },
    {
      title: 'Pending',
      value: stats?.pendingTasks || 0,
      icon: PendingIcon,
      color: '#9e9e9e',
      bgColor: '#f5f5f5',
    },
    {
      title: 'In Progress',
      value: stats?.inProgressTasks || 0,
      icon: InProgressIcon,
      color: '#2196f3',
      bgColor: '#e3f2fd',
    },
    {
      title: 'Completed',
      value: stats?.completedTasks || 0,
      icon: CompletedIcon,
      color: '#4caf50',
      bgColor: '#e8f5e8',
    },
    {
      title: 'Overdue',
      value: stats?.overdueTasks || 0,
      icon: OverdueIcon,
      color: '#f44336',
      bgColor: '#ffebee',
    },
  ];

  if (loading) {
    return (
      <Grid container spacing={3}>
        {[...Array(5)].map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" height={24} />
                    <Skeleton variant="text" width="40%" height={32} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      {statCards.map((stat, index) => {
        const IconComponent = stat.icon;
        return (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
            <Card
              sx={{
                borderRadius: 3,
                background: `linear-gradient(135deg, ${stat.bgColor} 0%, ${stat.bgColor}80 100%)`,
                border: `1px solid ${stat.color}20`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 25px ${stat.color}30`,
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        letterSpacing: '0.5px',
                        mb: 1
                      }}
                    >
                      {stat.title}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        color: stat.color,
                        fontSize: '2rem',
                        lineHeight: 1
                      }}
                    >
                      {stat.value.toLocaleString()}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      backgroundColor: `${stat.color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      ml: 2,
                    }}
                  >
                    <IconComponent
                      sx={{
                        fontSize: 28,
                        color: stat.color,
                      }}
                    />
                  </Box>
                </Box>

                {/* Progress indicator for completion rate */}
                {stat.title === 'Total Tasks' && stats?.totalTasks > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Completion Rate
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#4caf50' }}>
                        {Math.round((stats.completedTasks / stats.totalTasks) * 100)}%
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 4,
                        backgroundColor: '#e0e0e0',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${(stats.completedTasks / stats.totalTasks) * 100}%`,
                          height: '100%',
                          backgroundColor: '#4caf50',
                          borderRadius: 2,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {/* Overdue warning */}
                {stat.title === 'Overdue' && stat.value > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#f44336',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      ⚠️ Requires immediate attention
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default TaskStats;
